'use client'
import mapboxgl, { Map as TMap } from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import React from 'react'
import { atom } from 'nanostores'
import { useStore } from '@nanostores/react'
import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import { Breakpoint, breakpoints, useBreakpoints } from 'fuse_common/hooks/layout/use-breakpoints'
import { Route } from 'next'
import { useTypedPathname } from 'fuse_common/utils/routing/typed-navigation'
import { FuseLogo } from 'fuse_common/components/ui/icons/fuse'
import { Spinner } from 'fuse_common/components/ui/spinner'
import { colors, defaultAddressColor } from '@/utils/constants'
import { getSystemTheme, useTheme } from './app-theme'
import { premisesColorStore } from './stores/premises-color-store'

import './map.css'
import { initMapPaddingsDebugger } from './map-paddings-debugger'
import { StaticMap } from './static-map'
import { mapConfig } from './map-config'
import { premisesViewBottomSheetModeStore } from './premises-view/premises-view-bottom-sheet-mode'

type CoordinatesSet = [lat: number, long: number, zoom: number]
const mobileDefaultCoordinates = [52.5072, -4.276, 4.6] as CoordinatesSet
const desktopDefaultCoordinates = [55.000134, -8.287394, 5] as CoordinatesSet
const defaultCoordinates: { [breakpoint in Breakpoint]: CoordinatesSet } = {
  sm: mobileDefaultCoordinates,
  md: mobileDefaultCoordinates,
  lg: desktopDefaultCoordinates,
  xl: desktopDefaultCoordinates,
  '2xl': desktopDefaultCoordinates,
  '99xl': desktopDefaultCoordinates,
}

export const showMapOnMobileBecauseOfOnboardingStore = atom(true)
export const showMapOnMobileBecauseOfCustomerMenuStore = atom(true)
export const showMapOnMobileBecauseOfPremisesCardStore = atom(true)

const ensureIsNumber = (value: any, defaultValue: number) => {
  const parsed = Number(value)
  if (Number.isNaN(parsed)) return defaultValue
  return parsed
}

export const getDefaultCoordinates = (): CoordinatesSet => {
  if (!globalThis.window) return defaultCoordinates.lg

  const screen = (breakpoints.find(({ query }) => window.matchMedia(query).matches)?.name || 'lg') as Breakpoint
  return defaultCoordinates[screen]
}
type Padding = { top: number; right: number; bottom: number; left: number }
const defaultPadding: Padding = { top: 0, right: 0, bottom: 0, left: 0 }
const paddingsAreEqual = (a: Padding | null, b: Padding | null) =>
  a?.top === b?.top && a?.right === b?.right && a?.bottom === b?.bottom && a?.left === b?.left
const floatCoordinatesAreEqual = (a: number | null, b: number | null) =>
  a === b || (a !== null && b !== null && Math.abs(a - b) < 0.0001)

type MapMarker = { color: string; addressId?: string; coordinates?: [long: number, lat: number] }

export const mapCoordinatesStore = atom({
  visible: true,
  definedCoordinates: false,
  lat: defaultCoordinates.lg[0],
  long: defaultCoordinates.lg[1],
  zoom: defaultCoordinates.lg[2],
  showMarker: false as false | MapMarker[],
  showHomes: false,
  padding: defaultPadding,
  blur: true,
  showLogo: true,
  addressLabel: null as string | null,
  clipRect: false,
})

let mapInstance: TMap | null = null
let currentMapStyle: string | null = null
let lastLat: number | null = null
let lastLong: number | null = null
let lastZoom: number | null = null
let lastPadding: Padding | null = null

const colorTheme = {
  light: `mapbox://styles/${mapConfig.account}/${mapConfig.lightStyleId}?optimize=true`,
  dark: `mapbox://styles/${mapConfig.account}/${mapConfig.darkStyleId}?optimize=true`,
}
let markersToRemoveAfterAnimation: mapboxgl.Marker[] = []

const isWebglSupported = () => {
  let canvas: HTMLCanvasElement | null = null
  try {
    canvas = document.createElement('canvas')
    return !!window.WebGLRenderingContext && (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
  } catch {
    return false
  } finally {
    canvas?.remove()
  }
}

const mapboxUnsupportedStore = atom(false)
const initMapbox = (
  container: HTMLDivElement,
  style: string,
  coordinates: { lat: number; long: number; zoom: number },
) => {
  if (mapboxUnsupportedStore.get()) return
  if (!isWebglSupported()) {
    mapboxUnsupportedStore.set(true)
    return
  }
  try {
    return new mapboxgl.Map({
      container: container,
      style,
      center: [coordinates.long, coordinates.lat],
      zoom: coordinates.zoom,
      attributionControl: false,
      logoPosition: 'bottom-left',
      interactive: false,
    })
  } catch (error) {
    console.error('Failed to initialize mapbox:', error)
    mapboxUnsupportedStore.set(true)
    return
  }
}

export const Map: React.FC<{
  className?: string
  enforcedTheme?: 'light' | 'dark'
}> = ({ className: providedClassName, enforcedTheme }) => {
  const contextTheme = useTheme()
  const pathname = useTypedPathname()
  const theme = enforcedTheme || contextTheme
  const mapboxUnsupported = useStore(mapboxUnsupportedStore)

  const containerRef = React.useRef<HTMLDivElement>(null)
  const storeValue = useStore(mapCoordinatesStore)
  const { zoom, showMarker, padding, showHomes, blur, showLogo, addressLabel, clipRect, visible } = storeValue
  const lat = ensureIsNumber(storeValue.lat, getDefaultCoordinates()[0])
  const long = ensureIsNumber(storeValue.long, getDefaultCoordinates()[1])

  // TODO: get better ideas and refactor that shit 🤥
  const showMapOnMobileBecauseOfCustomerMenu = useStore(showMapOnMobileBecauseOfCustomerMenuStore)
  const showMapOnMobileBecauseOfPremisesCard = useStore(showMapOnMobileBecauseOfPremisesCardStore)
  const showMapOnMobileBecauseOfPremisesCardBottomSheetMode = useStore(premisesViewBottomSheetModeStore)
  const showMapOnMobileBecauseOfOnboarding = useStore(showMapOnMobileBecauseOfOnboardingStore)
  const showMapOnMobile = React.useMemo(() => {
    const onboardingScreens: Route[] = ['/switch-in', '/sign-in']
    if (!pathname) return false
    const onOnboarding = onboardingScreens.some((screen) => pathname.startsWith(screen))
    const onPropertiesScreen = pathname.startsWith('/premises')

    return (
      showMapOnMobileBecauseOfCustomerMenu &&
      (showMapOnMobileBecauseOfPremisesCard ||
        showMapOnMobileBecauseOfPremisesCardBottomSheetMode === 'enabled' ||
        !onPropertiesScreen) &&
      (showMapOnMobileBecauseOfOnboarding || !onOnboarding)
    )
  }, [
    pathname,
    showMapOnMobileBecauseOfPremisesCardBottomSheetMode,
    showMapOnMobileBecauseOfCustomerMenu,
    showMapOnMobileBecauseOfOnboarding,
    showMapOnMobileBecauseOfPremisesCard,
  ])

  const { isMobile, breakpoint, defined: breakpointsAreDefined } = useBreakpoints()
  const renderMap = React.useMemo(() => {
    if (!visible) return false
    if (!breakpointsAreDefined) return false
    return isMobile ? showMapOnMobile : true
  }, [breakpointsAreDefined, isMobile, showMapOnMobile, visible])
  const colorsOverride = useStore(premisesColorStore)
  React.useEffect(() => {
    const accessToken = mapConfig.accessToken
    if (mapboxUnsupported) return
    if (!accessToken) return
    if (!renderMap) return
    mapboxgl.accessToken = accessToken
    if (!theme) return
    if (!containerRef.current) return
    let resolvedTheme = theme
    if (resolvedTheme === 'system') {
      resolvedTheme = getSystemTheme()
    }
    const mapTheme = resolvedTheme as 'light' | 'dark'

    if (!mapInstance) {
      const style = colorTheme[mapTheme]
      mapInstance = initMapbox(containerRef.current, style, { lat, long, zoom })!
      if (!mapInstance) return

      mapInstance.setPadding(padding)
      currentMapStyle = style
      mapInstance.on('movestart', () => {
        const showMarker = mapCoordinatesStore.get().showMarker
        const allMarkersAreGoingToBeRemoved = !showMarker || (Array.isArray(showMarker) && showMarker.length === 0)
        if (!allMarkersAreGoingToBeRemoved) return

        markersToRemoveAfterAnimation.forEach((marker) => {
          marker.addClassName('transition-opacity')
          marker.addClassName('duration-100')
          marker.addClassName('opacity-100')
        })
        setTimeout(() => {
          markersToRemoveAfterAnimation.forEach((marker) => {
            marker.addClassName('!opacity-0')
          })
        }, 0)
      })
      mapInstance.on('moveend', () => {
        markersToRemoveAfterAnimation.forEach((marker) => marker?.remove())
        markersToRemoveAfterAnimation.length = 0
      })
      mapInstance.on('style.load', () => {
        if (!mapInstance) return
        const showHomes = mapCoordinatesStore.get().showHomes
        const homesLayerVisible = mapInstance.getLayoutProperty('homes', 'visibility') === 'visible'
        if (homesLayerVisible !== showHomes) {
          mapInstance.setLayoutProperty('homes', 'visibility', showHomes ? 'visible' : 'none')
        }
      })
      mapCoordinatesStore.subscribe(({ showHomes }) => {
        if (!mapInstance) return
        if (!mapInstance._loaded) return
        if (!mapInstance.style._loaded) return
        const homesLayerVisible = mapInstance.getLayoutProperty('homes', 'visibility') === 'visible'
        if (homesLayerVisible !== showHomes) {
          mapInstance.setLayoutProperty('homes', 'visibility', showHomes ? 'visible' : 'none')
        }
      })
      mapInstance.on('error', (error) => {
        const errorMessage = error?.error?.message
        if (!errorMessage) {
          console.error('Unexpected mapbox error:', error)
          return
        }
        const failedToFetchVectorError =
          (errorMessage.startsWith('Failed to fetch https://api.mapbox.com/v4/') ||
            errorMessage.startsWith('Load failed https://api.mapbox.com/v4/')) &&
          errorMessage.includes('.vector.pbf')

        if (failedToFetchVectorError) {
          // eslint-disable-next-line no-console
          console.warn('Mapbox error:', error)
        } else {
          console.error('Unexpected mapbox error:', error)
        }
      })
    } else {
      if (
        !floatCoordinatesAreEqual(lat, lastLat) ||
        !floatCoordinatesAreEqual(long, lastLong) ||
        zoom !== lastZoom ||
        !paddingsAreEqual(padding, lastPadding)
      ) {
        mapInstance.flyTo({
          center: [long, lat],
          zoom,
          duration: 1000,
          padding,
        })
      }
      if (currentMapStyle !== colorTheme[mapTheme]) {
        mapInstance.setStyle(colorTheme[mapTheme])
        currentMapStyle = colorTheme[mapTheme]
      }
    }
    lastLat = lat
    lastLong = long
    lastZoom = zoom
    lastPadding = padding

    let markerInstances: mapboxgl.Marker[] = []

    for (const marker of showMarker || []) {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
      const borderWidth = 5
      const size = 65
      svg.setAttribute('viewBox', `0 0 ${size} ${size}`)
      const color =
        ('addressId' in marker ? colors[colorsOverride[marker.addressId || ''] || marker.color] : marker.color) ||
        colors[defaultAddressColor]
      {
        const backgroundCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
        backgroundCircle.setAttribute('cx', size / 2 + '')
        backgroundCircle.setAttribute('cy', size / 2 + '')
        backgroundCircle.setAttribute('r', size / 2 + '')
        backgroundCircle.setAttribute('fill', color)
        backgroundCircle.setAttribute('fill-opacity', '0.1')
        svg.appendChild(backgroundCircle)
      }
      {
        const mainCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
        mainCircle.setAttribute('cx', size / 2 + '')
        mainCircle.setAttribute('cy', size / 2 + '')
        mainCircle.setAttribute('r', size / 5 - borderWidth + '')
        mainCircle.setAttribute('stroke-width', borderWidth + '')
        mainCircle.setAttribute('stroke', color)
        mainCircle.setAttribute('fill', color)
        mainCircle.setAttribute('fill-opacity', '0.5')
        svg.appendChild(mainCircle)
      }
      const markerIcon = document.createElement('div')
      {
        markerIcon.style.width = size + 'px'
        markerIcon.style.height = size + 'px'
        svg.style.width = size + 'px'
        svg.style.height = size + 'px'
        markerIcon.appendChild(svg)
      }

      if (addressLabel) {
        const labelBelow = document.createElement('div')
        labelBelow.id = `map-marker-label-below-${addressLabel.replace(/[^\w]/g, '').toLowerCase()}`
        labelBelow.textContent = addressLabel
        labelBelow.className =
          'text-black bg-white px-2 py-1 rounded-small text-body2-paragraph w-[160px] text-center relative top-2 shadow-raised transition-opacity duration-500'
        labelBelow.style.left = (160 - size) / -2 + 'px'
        if (document.getElementById(labelBelow.id) === null) {
          labelBelow.style.opacity = '0'
          setTimeout(() => {
            labelBelow.style.opacity = '1'
          }, 0)
        }
        markerIcon.appendChild(labelBelow)
      }

      markersToRemoveAfterAnimation.forEach((marker) => marker?.remove())
      const markerLong = ensureIsNumber(marker.coordinates?.[0]!, long)
      const markerLat = ensureIsNumber(marker.coordinates?.[1]!, lat)
      markerInstances.push(new mapboxgl.Marker(markerIcon).setLngLat([markerLong, markerLat]).addTo(mapInstance))
    }
    return () => {
      markersToRemoveAfterAnimation.push(...markerInstances)
    }
  }, [
    lat,
    long,
    zoom,
    theme,
    showMarker,
    padding,
    showHomes,
    colorsOverride,
    addressLabel,
    renderMap,
    mapboxUnsupported,
  ])
  const disposeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const disposeMap = React.useCallback(() => {
    clearTimeout(disposeTimeoutRef.current!)
    disposeTimeoutRef.current = setTimeout(() => {
      if (!mapInstance) return
      markersToRemoveAfterAnimation.forEach((marker) => marker?.remove())
      markersToRemoveAfterAnimation.length = 0
      mapInstance?.remove()
      mapInstance = null
    }, 500)
  }, [])
  React.useEffect(() => {
    if (!renderMap && mapInstance) disposeMap()
  }, [renderMap, disposeMap])
  React.useEffect(() => {
    return () => disposeMap()
  }, [disposeMap])
  React.useEffect(() => {
    initMapPaddingsDebugger()
  }, [])

  React.useEffect(() => {
    if (mapboxUnsupported) return
    let timeout: ReturnType<typeof setTimeout> | undefined = undefined
    const handleWindowResize = () => {
      clearTimeout(timeout)
      timeout = setTimeout(() => {
        mapInstance?.resize()
      }, 300)
    }
    window.addEventListener('resize', handleWindowResize)
    return () => {
      clearTimeout(timeout)
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [mapboxUnsupported])

  const className = clsx(providedClassName, clipRect ? '!absolute' : '!fixed')
  const style = React.useMemo(() => {
    if (!clipRect)
      return {
        clipPath: 'rect(0 100% 100% 0 round 0%)',
      }
    let xOffset = 0
    let yOffset = 0
    if (isMobile) {
      return {
        clipPath: `xywh(0px 0px 100svw 100svh round 0px)`,
      }
    } else if (breakpoint === 'lg' || breakpoint === 'xl' || breakpoint === '2xl') {
      xOffset = 24
      yOffset = 104
    } else {
      xOffset = 150
      yOffset = 104
    }
    return {
      clipPath: clipRect
        ? `rect(${yOffset}px calc(100svw - ${xOffset}px) calc(100svh - ${yOffset}px) ${xOffset}px round 40px)`
        : 'rect(0 100% 100% 0 round 0%)',
    }
  }, [clipRect, isMobile, breakpoint])
  const mapOverlayStyle = React.useMemo(
    () => ({
      ...style,
      background:
        'radial-gradient(125.7% 80.6% at 50% 30.5%, rgba(10, 93, 255, 0.00) 45.98%, rgba(49, 79, 136, 0.24) 64.93%)',
    }),
    [style],
  )

  return (
    <>
      <div
        className={clsx(
          'top-0 left-0 size-full -z-20', // Position behind map container
          className, // Apply same positioning class (fixed/absolute)
          // Use neutral background colors based on the theme
          'bg-map-background',
          renderMap ? '!opacity-100 delay-150' : '!opacity-0',
        )}
        style={style} // Apply same clipRect style
      />
      <AnimatePresence>
        <div
          key="map-container"
          ref={containerRef}
          className={clsx(
            'transition-all top-0 left-0 size-full -z-10',
            className,
            blur ? 'blur-xl' : 'blur-0',
            renderMap ? '!opacity-100 delay-150' : '!opacity-0',
          )}
          style={style}
          suppressHydrationWarning
        />
        {mapboxUnsupported && (
          <StaticMap key="static-map-fallback" lon={long} lat={lat} zoom={zoom} className={className} style={style} />
        )}
        {showLogo && (
          <motion.div
            className="fixed top-0 left-0 flex items-center justify-center w-full h-full"
            exit={logoContainerExit}
            initial={logoContainerInitial}
            animate={logoContainerAnimate}
            key="logo-container"
          >
            <FuseLogo className="w-10 h-10" />
            <motion.div
              className="opacity-0"
              initial={spinnerInitial}
              animate={spinnerAnimate}
              transition={spinnerTransition}
            >
              <Spinner className="ml-4 " />
            </motion.div>
          </motion.div>
        )}
        <div
          className={clsx(
            'transition-all top-0 left-0 size-full -z-10 pointer-events-none max-w-[100svw] max-h-[100svh]',
            className,
            renderMap ? '!opacity-100 delay-150' : '!opacity-0',
          )}
          style={mapOverlayStyle}
        />
      </AnimatePresence>
    </>
  )
}

const logoContainerExit = { opacity: 0 }
const logoContainerInitial = { opacity: 0 }
const logoContainerAnimate = { opacity: 1 }
const spinnerInitial = { opacity: 0, width: 0 }
const spinnerAnimate = { opacity: 0.5, width: 'auto' }
const spinnerTransition = { type: 'ease-in-out', duration: 0.5, delay: 1 }
